import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Card, CardContent, CircularProgress, IconButton, Paper, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, Typography, Button,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCartOutlined";
import HourglassTopIcon from "@mui/icons-material/HourglassTopOutlined";
import PaidIcon from "@mui/icons-material/PaidOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmberOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFileOutlined";
import GroupAddIcon from "@mui/icons-material/GroupAddOutlined";
import BarChartIcon from "@mui/icons-material/BarChartOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import dayjs from "dayjs";

import StatCard from "./StatCard";
import StatusChip from "../components/StatusChip";
import OrderDetailsDialog from "../components/OrderDetailsDialog";
import { BRAND } from "../theme";
import { useAuth } from "../auth/AuthContext";
import { OrderVM, OrderStatus } from "../data/types";
import { toOrderVM, toProductVM } from "../data/mappers";
import { formatCAD, formatCADLong } from "../utils/currency";
import { fetchOrders, fetchProducts, updateOrderStatus } from "../data/api";

const LOW_STOCK_THRESHOLD = 10;
const fmtDate = (d?: string) => (d ? dayjs(d).format("MMM D, YYYY") : "—");

const DistributorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { username } = useAuth();
  const [orders, setOrders] = useState<OrderVM[]>([]);
  const [lowStock, setLowStock] = useState(0);
  const [loading, setLoading] = useState(true);
  const [reachable, setReachable] = useState(true);

  const load = useCallback(async () => {
    const [ordRaw, prodRaw] = await Promise.all([fetchOrders(), fetchProducts()]);
    const ord = (ordRaw || []).map(toOrderVM);
    const prod = (prodRaw || []).map(toProductVM);
    setOrders(ord);
    setLowStock(prod.filter((p) => p.stock <= LOW_STOCK_THRESHOLD).length);
    if ((ordRaw || []).length === 0 && (prodRaw || []).length === 0) setReachable(false);
    return ord;
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { await load(); } catch { setReachable(false); } finally { setLoading(false); }
    })();
  }, [load]);

  const [selected, setSelected] = useState<OrderVM | null>(null);
  const changeStatus = async (next: OrderStatus) => {
    if (!selected) return;
    await updateOrderStatus(selected.id, next);
    const fresh = await load();
    setSelected(fresh.find((o) => o.id === selected.id) ?? null);
  };

  const metrics = useMemo(() => {
    const today = dayjs().format("YYYY-MM-DD");
    const todays = orders.filter((o) => o.orderDate && dayjs(o.orderDate).format("YYYY-MM-DD") === today);
    const pending = orders.filter((o) => o.status === "Pending" || o.status === "New");
    const revenue = orders.reduce((s, o) => s + (o.total || 0), 0);
    return { todays: todays.length, pending: pending.length, revenue };
  }, [orders]);

  const recent = useMemo(
    () => [...orders].sort((a, b) => dayjs(b.orderDate || 0).valueOf() - dayjs(a.orderDate || 0).valueOf()).slice(0, 8),
    [orders]
  );

  return (
    <Box>
      <Typography variant="h4">Dashboard</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Welcome back{username ? `, ${username}` : ""}! Here's what's happening with your business today.
      </Typography>

      {!reachable && !loading && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: "#fff8e1", borderColor: "#f0e0a0" }}>
          <Typography variant="body2">
            No data returned from the backend yet. Metrics will populate once the API is running and seeded.
          </Typography>
        </Paper>
      )}

      {/* Metric cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4,1fr)" }, gap: 2, mb: 3 }}>
        <StatCard label="Today's Orders" value={loading ? "—" : metrics.todays} delta="20%" deltaNote="vs. last week"
          icon={<ShoppingCartIcon fontSize="small" />} iconBg="#e8f0fe" iconColor="#1a56c4" />
        <StatCard label="Pending Approval" value={loading ? "—" : metrics.pending} delta={`${metrics.pending}`} deltaNote="awaiting your review"
          deltaColor="warning" icon={<HourglassTopIcon fontSize="small" />} iconBg="#fdf0d5" iconColor="#b26a00" />
        <StatCard label="Revenue (CAD)" value={loading ? "—" : formatCADLong(metrics.revenue)} delta="18%" deltaNote="total to date"
          icon={<PaidIcon fontSize="small" />} iconBg="#e7f3ec" iconColor={BRAND.green} />
        <StatCard label="Low Stock Items" value={loading ? "—" : lowStock} delta={`${lowStock}`} deltaNote="need attention"
          deltaColor="error" icon={<WarningAmberIcon fontSize="small" />} iconBg="#fde8e8" iconColor="#c0392b" />
      </Box>

      {/* Recent orders */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6">Recent Orders</Typography>
            <Button size="small" onClick={() => navigate("/d/orders")}>View all orders →</Button>
          </Stack>
          {loading ? (
            <Box sx={{ display: "grid", placeItems: "center", py: 5 }}><CircularProgress /></Box>
          ) : recent.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>No orders yet.</Typography>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Order #", "Customer", "Items", "Order Date", "Delivery Date", "Total (CAD)", "Status", ""].map((h) => (
                      <TableCell key={h} sx={{ color: "text.secondary", fontWeight: 700 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recent.map((o) => (
                    <TableRow key={o.id} hover onClick={() => setSelected(o)} sx={{ cursor: "pointer" }}>
                      <TableCell sx={{ fontWeight: 600, color: "primary.main" }}>{o.orderId}</TableCell>
                      <TableCell>{o.customer}</TableCell>
                      <TableCell>{o.items}</TableCell>
                      <TableCell>{fmtDate(o.orderDate)}</TableCell>
                      <TableCell>{fmtDate(o.deliveryDate)}</TableCell>
                      <TableCell>{formatCAD(o.total)}</TableCell>
                      <TableCell><StatusChip status={o.status} /></TableCell>
                      <TableCell align="right"><IconButton size="small" onClick={(e) => { e.stopPropagation(); setSelected(o); }}><MoreVertIcon fontSize="small" /></IconButton></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Quick actions */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3,1fr)" }, gap: 2 }}>
        <QuickAction icon={<UploadFileIcon />} title="Import Product Catalogue"
          desc="Upload a CSV file to add or update products quickly." onClick={() => navigate("/d/products")} />
        <QuickAction icon={<GroupAddIcon />} title="Manage Customers"
          desc="Add new customers and set custom pricing." onClick={() => navigate("/d/customers")} />
        <QuickAction icon={<BarChartIcon />} title="View Reports"
          desc="Sales, top products and customer insights." onClick={() => navigate("/d/reports")} />
      </Box>

      <OrderDetailsDialog
        open={!!selected}
        order={selected}
        onClose={() => setSelected(null)}
        canManage
        onStatusChange={changeStatus}
      />
    </Box>
  );
};

const QuickAction: React.FC<{ icon: React.ReactElement; title: string; desc: string; onClick: () => void }> = ({ icon, title, desc, onClick }) => (
  <Card onClick={onClick} sx={{ cursor: "pointer", bgcolor: BRAND.greenSoft, border: "none", "&:hover": { boxShadow: 2 } }}>
    <CardContent>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ color: BRAND.green, bgcolor: "#fff", width: 40, height: 40, borderRadius: 1.5, display: "grid", placeItems: "center" }}>{icon}</Box>
        <Box>
          <Typography variant="subtitle2">{title}</Typography>
          <Typography variant="caption" color="text.secondary">{desc}</Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

export default DistributorDashboard;
